import { FormData } from './types';
import { supabaseEventSeriesService } from '../../../supabase/eventSeries.service';
import { EventSeries, SeriesEvent, RecurrenceChangePreview, RecurrenceChangeResult } from '../../../types/eventSeries.types';
import { createTimestamp } from '../../../utils/dateUtils';
import toast from 'react-hot-toast';

// Type pour le résultat de mise à jour avec confirmation potentielle
export interface UpdateRecurringResult {
  success: boolean;
  requiresConfirmation?: boolean;
  preview?: RecurrenceChangePreview;
  recurrenceResult?: RecurrenceChangeResult;
}

/**
 * 📊 Calcule le nombre d'occurrences attendues pour affichage dans le toast
 */
function calculateExpectedOccurrences(formData: FormData): number {
  // Si endType est 'occurrences', multiplier par le nombre de jours sélectionnés (weekly)
  // Ex: 3 semaines × 2 jours (Mon, Wed) = 6 occurrences au total
  if (formData.endType === 'occurrences' && formData.occurrences) {
    if (formData.frequency === 'weekly' && formData.weekDays?.length) {
      return formData.occurrences * formData.weekDays.length;
    }
    return formData.occurrences;
  }

  // Si endType est 'date', on calcule basé sur la fréquence
  if (formData.endType === 'date' && formData.startDate && formData.endDate) {
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (formData.frequency === 'daily') {
      return Math.max(1, daysDiff + 1);
    } else if (formData.frequency === 'weekly' && formData.weekDays?.length) {
      // Estimation: nombre de semaines * jours sélectionnés par semaine
      const weeks = Math.ceil(daysDiff / 7);
      return Math.max(1, weeks * formData.weekDays.length);
    }
  }

  // Valeur par défaut si on ne peut pas calculer
  return 1;
}

/**
 * When daySchedules is active, derives startTime from the first day's schedule
 * and auto-fills missing end times (start + 1h).
 */
function normalizeDaySchedules(formData: FormData): { startTime?: string; endTime?: string; daySchedules: FormData['daySchedules'] } {
  const ds = formData.daySchedules;
  if (!ds) return { startTime: formData.startTime, endTime: formData.endTime, daySchedules: null };

  // Auto-fill missing end times: start + 1h
  const normalized: Record<string, { start: string; end: string }> = {};
  for (const [day, schedule] of Object.entries(ds)) {
    if (!schedule?.start) continue;
    let end = schedule.end;
    if (!end) {
      // Parse start time and add 1 hour
      const [h, m] = schedule.start.split(':').map(Number);
      const endH = String((h + 1) % 24).padStart(2, '0');
      end = `${endH}:${String(m).padStart(2, '0')}`;
    }
    normalized[day] = { start: schedule.start, end };
  }

  // Derive startTime from the first day's schedule (for backward compatibility)
  const firstDay = formData.weekDays?.find(d => normalized[d]);
  const derivedStartTime = firstDay ? normalized[firstDay].start : formData.startTime;
  const derivedEndTime = firstDay ? normalized[firstDay].end : formData.endTime;

  return {
    startTime: formData.startTime || derivedStartTime,
    endTime: formData.endTime || derivedEndTime,
    daySchedules: normalized,
  };
}

export async function handleRecurringEvent(formData: FormData): Promise<string> {
  // Normalize daySchedules: derive startTime, auto-fill missing endTimes
  const { startTime, endTime, daySchedules } = normalizeDaySchedules(formData);

  // Convertir les données du formulaire en format EventSeries
  const seriesData: Omit<EventSeries, 'id'> = {
    organizerId: formData.organizerId,
    organizerContact: formData.organizerContact,
    timezone: formData.timezone,
    title: formData.title,
    description: formData.description,
    location: formData.location,
    locationName: formData.locationName,
    latitude: formData.latitude,
    longitude: formData.longitude,
    geohash: formData.geohash, // Geohash calculé côté client
    tags: formData.tags,
    image: formData.image,
    prices: formData.prices,
    ticketingUrl: formData.ticketingUrl || '',
    status: formData.status,
    
    // Données de récurrence
    frequency: formData.frequency,
    startDate: formData.startDate,
    startTime: startTime,
    endTime: endTime,
    endType: formData.endType,
    endDate: formData.endDate,
    occurrences: formData.occurrences,
    weekDays: formData.weekDays,
    daySchedules: daySchedules,

    // Métadonnées
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
  };

  // 📊 Calculer le nombre d'occurrences attendues AVANT création
  const expectedOccurrences = calculateExpectedOccurrences(formData);
  const isDraft = seriesData.status === 'draft';

  // 🔄 Toast de loading avec compteur d'occurrences
  const toastId = toast.loading(
    `🔄 Creating your recurring event series...\n\n📅 Generating ${expectedOccurrences} occurrence${expectedOccurrences > 1 ? 's' : ''}...\n\n⏳ This may take a few seconds.`,
    {
      style: {
        borderRadius: '12px',
        background: 'linear-gradient(to bottom, #E3F2FD, #F8F9FF)',
        color: '#1565C0',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '12px 16px',
        maxWidth: '380px',
      }
    }
  );

  // Créer la série d'événements
  try {
    const seriesId = await supabaseEventSeriesService.createEventSeries(seriesData);
    console.log("📅 Série créée avec succès, ID:", seriesId);

    // 🎉 Toast de succès avec le nombre d'occurrences créées
    toast.success(
      `🎉 Recurring series created!\n\n✨ ${expectedOccurrences} occurrence${expectedOccurrences > 1 ? 's' : ''} ${isDraft ? 'saved as draft' : 'are now live'}!\n\n${isDraft ? '📝 Ready to publish when you are!' : '🚀 Your audience can now discover your events!'}`,
      {
        id: toastId,
        duration: 6000,
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(to bottom, #E8F5E8, #F0FFF0)',
          color: '#2E7D2E',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          padding: '12px 16px',
          maxWidth: '380px',
        }
      }
    );

    return seriesId;
  } catch (error) {
    console.error('Erreur lors de la création de la série:', error);

    // 🚨 Toast d'erreur
    toast.error(
      `🐒 Oops! Our monkeys ran into trouble creating your recurring series.\n\nPlease check your information and try again! 🍌`,
      {
        id: toastId,
        duration: 5000,
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(to bottom, #FFEBEE, #FFF8F8)',
          color: '#C62828',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          padding: '12px 16px',
          maxWidth: '380px',
        }
      }
    );

    throw error;
  }
}

interface EventUpdateData extends Partial<SeriesEvent> {
  updatedAt: Date;
}

// Fonction pour mettre à jour un événement récurrent
export async function updateRecurringEvent(
  seriesId: string,
  formData: FormData,
  updateAll: boolean = true,
  options: { skipConfirmation?: boolean; showToast?: boolean } = {}
): Promise<UpdateRecurringResult> {
  console.log('🔄 [updateRecurringEvent] Début mise à jour série:', seriesId);

  const { skipConfirmation = false, showToast = true } = options;

  // Normalize daySchedules: derive startTime, auto-fill missing endTimes
  const normalized = normalizeDaySchedules(formData);

  // 🔄 Toast de loading pour la mise à jour (seulement si showToast)
  const toastId = showToast ? toast.loading(
    `🔄 Updating your recurring event series...\n\n⏳ Please wait while we update all occurrences...`,
    {
      style: {
        borderRadius: '12px',
        background: 'linear-gradient(to bottom, #E3F2FD, #F8F9FF)',
        color: '#1565C0',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '12px 16px',
        maxWidth: '380px',
      }
    }
  ) : undefined;

  try {
    const baseUpdateData: EventUpdateData = {
      title: formData.title,
      description: formData.description,
      location: formData.location,
      locationName: formData.locationName,
      latitude: formData.latitude,
      longitude: formData.longitude,
      tags: formData.tags,
      image: formData.image,
      prices: formData.prices,
      ticketingUrl: formData.ticketingUrl || '',
      status: formData.status,

      updatedAt: createTimestamp()
    };

    if (updateAll) {
      // Mettre à jour toute la série
      const seriesUpdateData: Partial<EventSeries> = {
        ...baseUpdateData,
        frequency: formData.frequency,
        startDate: formData.startDate,
        startTime: normalized.startTime,
        endTime: normalized.endTime,
        endType: formData.endType,
        endDate: formData.endDate,
        occurrences: formData.occurrences,
        weekDays: formData.weekDays,
        daySchedules: normalized.daySchedules,
      };

      // Si pas de skipConfirmation, prévisualiser d'abord les changements
      if (!skipConfirmation) {
        const preview = await supabaseEventSeriesService.previewRecurrenceChange(seriesId, seriesUpdateData);

        // Si des occurrences vont être supprimées, demander confirmation
        if (preview.toDelete.length > 0) {
          console.log('⚠️ [updateRecurringEvent] Confirmation requise:', preview);

          // Dismiss le toast de loading si présent
          if (toastId) {
            toast.dismiss(toastId);
          }

          return {
            success: false,
            requiresConfirmation: true,
            preview
          };
        }
      }

      // Log pour débogage
      console.log('Mise à jour de la série avec les données:', seriesUpdateData);

      console.log('📤 [updateRecurringEvent] Envoi de la mise à jour...');
      const recurrenceResult = await supabaseEventSeriesService.updateEventSeries(seriesId, seriesUpdateData);
      console.log('✅ [updateRecurringEvent] Mise à jour réussie');

      // 🎉 Toast de succès pour mise à jour de TOUTE la série
      if (showToast) {
        const isDraft = formData.status === 'draft';
        const statusText = isDraft ? 'draft' : 'published';

        // Message différent si des occurrences ont été modifiées
        let successMessage = `🔄✨ Event series updated!\n\nAll events in your recurring series have been successfully updated and ${statusText}.`;

        if (recurrenceResult && (recurrenceResult.deleted > 0 || recurrenceResult.created > 0)) {
          const changes: string[] = [];
          if (recurrenceResult.deleted > 0) {
            changes.push(`${recurrenceResult.deleted} removed`);
          }
          if (recurrenceResult.created > 0) {
            changes.push(`${recurrenceResult.created} created`);
          }
          successMessage = `🔄✨ Event series updated!\n\n📅 Occurrences: ${changes.join(', ')}\n\nYour series has been synchronized!`;
        }

        toast.success(successMessage, {
          id: toastId,
          duration: 6000,
          style: {
            borderRadius: '12px',
            background: 'linear-gradient(to bottom, #E8F5E8, #F0FFF0)',
            color: '#2E7D2E',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '12px 16px',
            maxWidth: '350px'
          }
        });
      }

      // Log après mise à jour
      console.log('Série mise à jour avec succès');

      return {
        success: true,
        recurrenceResult: recurrenceResult || undefined
      };
    } else if (formData.id) {
      // Mettre à jour uniquement l'événement spécifique
      const singleEventUpdateData: EventUpdateData = {
        ...baseUpdateData,
        isException: true // Marquer comme exception car modifié individuellement
      };

      // Si la date ou l'heure est modifiée, mettre à jour datetime
      if (formData.date && formData.time) {
        const [year, month, day] = formData.date.split('-').map(Number);
        const [hours, minutes] = formData.time.split(':').map(Number);
        singleEventUpdateData.datetime = new Date(year, month - 1, day, hours, minutes);
      }

      await supabaseEventSeriesService.updateSingleEvent(formData.id, singleEventUpdateData);

      // 🎉 Toast de succès pour mise à jour d'UN SEUL événement
      if (showToast) {
        const isDraftSingle = formData.status === 'draft';
        const statusTextSingle = isDraftSingle ? 'saved as draft' : 'published';

        toast.success(
          `📝✨ Single event updated!\n\nThis specific event has been successfully updated and ${statusTextSingle}. Other events in the series remain unchanged.`,
          {
            id: toastId,
            duration: 5000,
            style: {
              borderRadius: '12px',
              background: 'linear-gradient(to bottom, #FFF3E0, #FFFAF5)',
              color: '#E65100',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              padding: '12px 16px',
              maxWidth: '350px'
            }
          }
        );
      }

      return { success: true };
    }

    return { success: true };
  } catch (error) {
    console.error('❌ [updateRecurringEvent] Erreur:', error);

    // 🚨 Toast d'erreur pour mise à jour récurrente
    if (showToast) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(
        `🐒 Update failed: ${errorMessage.substring(0, 100)}`,
        {
          id: toastId,
          duration: 8000,
          style: {
            borderRadius: '12px',
            background: 'linear-gradient(to bottom, #FFEBEE, #FFF8F8)',
            color: '#C62828',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '12px 16px',
            maxWidth: '400px'
          }
        }
      );
    }

    throw error;
  }
}

